/*
 * File: App.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Initializes the application
 */
import React, { RefAttributes } from "react";
import { Redirect, Route } from "react-router";
import { Theme, withStyles } from "@material-ui/core/styles";
import AppStore from "./Store";
import Routes from "./Routes";
import NavBar from "./components/NavBar";
import Router from "./components/Router";
import RenderLogin from "./services/login/views/RenderLogin";
import LoginApp from "./services/login";
import LoginStore from "./services/login/Store";
import APIErrorDialog from "./components/APIErrorDialog";
import "css/App.css";
import StandAloneNav from "components/StandAloneNav";
import CheckIndex from "utils/CheckIndex";
import DefaultRedirect from "components/DefaultRedirect";
import PubSub from "pubsub-js";
import "./utils/MPArrayExtension";
import UIProgress from "components/ui/UIProgress";
import { preferencePreload } from "services/preferences/Store";
import backgroundImage from "images/MPMobileBackground.png";
import UIAlertDialog from "components/ui/UIAlertDialog";
import { StoreContext, StoreManager } from "utils/useStore";
import { preloadOrderReferences } from "services/orders/store/OrderReferences";
import Users from "services/users/Users";
import { setInterceptorStore } from "api/config";
import { createStyles, WithStyles } from "@material-ui/styles";
import { ErrorBoundary } from "ErrorBoundary";

const styles = (theme: Theme) =>
  createStyles({
    appFrame: {
      zIndex: 1,
      position: "relative",
      display: "flex",
      width: "100%",
      height: "100%"
    },
    toolbar: theme.mixins.toolbar,
    content: {
      flex: "1 1 100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.background.default,
      maxWidth: "100%",
      [theme.breakpoints.up("md")]: {
        maxWidth: "calc(100% - 240px)"
      }
    },
    loadingBackground: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      padding: theme.spacing(2.5),
      paddingTop: 0,
      paddingBottom: 0,
      backgroundImage: `url(${backgroundImage})`,
      backgroundDimension: "contain"
    }
  });

/**
 * An array of async functions which each perform some preloading task that must be completed before the app is ready for use.
 */
const startupPreLoaders = [preferencePreload, preloadOrderReferences];

type AppState = {
  jwt: string | null;
  preloadPromise: Promise<void> | null;
  didPreLoad: boolean;
  preloadFailed: boolean;
  storeManager: StoreManager;
};

// Recieves style props and optionally a ref to store a component reference in
type Props = WithStyles<typeof styles> & RefAttributes<App>;

class App extends React.Component<Props, AppState> {
  subscription: string;

  constructor(props: Props) {
    super(props);
    this.state = {
      jwt: LoginStore.getJwt(),
      preloadPromise: null,
      didPreLoad: false,
      preloadFailed: false,
      storeManager: new StoreManager()
    };
    this.subscription = PubSub.subscribe("LOGGING_OUT", () => {
      console.log("Resetting app state");
      this.resetPreloadState();
      this.updateJwt();
      this.setState(prev => ({ ...prev, storeManager: new StoreManager() }));
    });
  }

  get appStore() {
    return this.state.storeManager.getStore(AppStore);
  }

  componentDidMount() {
    if (this.state.jwt != null && this.state.preloadPromise === null) {
      this.startPreload();
    }
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this.subscription);
  }

  updateJwt() {
    this.setState(prev => ({ ...prev, jwt: LoginStore.getJwt() }));

    this.startPreload();
  }

  /**
   * Resets the component's preloading state back to the initial values.
   * @param callback An optional callback to pass to the setState method
   */
  resetPreloadState(callback?: () => void) {
    this.setState(
      prev => ({
        ...prev,
        didPreLoad: false,
        preloadFailed: false,
        preloadPromise: null
      }),
      callback
    );
  }

  /**
   * Start preload actions, and update state to indicate loading has started.
   * Note that this method explicitly overrides the default API error dialogue in order to display a message and a retry button in the render method.
   */
  startPreload() {
    if (LoginStore.getJwt() != null && !this.state.didPreLoad) {
      console.log("Preloading");
      if (this.state.preloadPromise === null) {
        const promise = (async () => {
          for (const action of startupPreLoaders) {
            await action(this.state.storeManager);
          }
        })();

        this.setState(prev => ({ ...prev, preloadPromise: promise }));

        promise
          .then(() => {
            console.log("Preload finished");
            this.setState(prev => ({
              ...prev,
              didPreLoad: true,
              preloadPromise: null
            }));
          })
          .catch(reason => {
            console.error("Preloading failed", reason);
            this.setState(prev => ({
              ...prev,
              didPreLoad: false,
              preloadFailed: true
            }));

            // Reset the api response error value to avoid showing the API error dialog after preloading succeeds, since we already display an error UI.
            this.appStore.apiResponseError(undefined);
          });
      }
    }
  }

  /**
   * Called if a jwt was found, meaning the user is valid
   * until the API says otherwise
   *
   * We're setting the user in the App store and creating
   * service stores to pass in the router for sub apps to use
   */
  validUser = () => {
    const { classes } = this.props;
    this.appStore.setUser(this.state.storeManager.getStore(Users));

    if (!this.state.didPreLoad) {
      // If preloading hasn't completed yet, show a loader. It should not be possible to reach this point without preloading starting.
      return (
        <div className={classes.loadingBackground}>
          <UIProgress fullScreen />
        </div>
      );
    }

    if (CheckIndex.isIndex()) {
      return <Redirect to={CheckIndex.redirectTo()} />;
    }

    return (
      <>
        <LoginApp />
        {/* This error boundary exists to catch errors before they bubble up past the keepalive handler in LoginApp. If errors occur deep in the component tree of an ability, this boundary will catch them while still allowing keepalive requests to proceed normally. Without this boundary, the top level boundary in src/index.tsx would catch the errors and the JWT would expire instead of being refreshed as expected. If any errors occur outside of this boundary (for example while setting up the keepalive timer) the top level boundary will catch them instead of this one.*/}
        <ErrorBoundary>
          <div className={classes.appFrame}>
            <NavBar store={this.appStore} />
            <main className={classes.content}>
              <div className={classes.toolbar} />

              {/* Router content */}
              <Router routes={Routes} />
              <StandAloneNav />
            </main>
          </div>
        </ErrorBoundary>
      </>
    );
  };

  render() {
    setInterceptorStore(this.appStore);

    let preloadErrorView = null;

    if (this.state.preloadFailed) {
      // Since preloading blocks the user from proceeding if it fails, display an alert that allows them to retry instead of just leaving the spinner there until they refresh the page.
      preloadErrorView = (
        <div className={this.props.classes.loadingBackground}>
          <UIAlertDialog
            open={true}
            errorTitle="Loading Failed"
            errorMessage="An error occured when loading app data. Please try again."
            buttonTitle="Retry"
            handleClose={() => {
              if (this.state.preloadFailed) {
                this.resetPreloadState(() => {
                  this.startPreload();
                });
              }
            }}
          />
        </div>
      );
    }

    return (
      <StoreContext.Provider value={this.state.storeManager}>
        {// Display the error view if it exists, otherwise display the standard UI.
        preloadErrorView || (
          <React.Fragment>
            <Route
              render={() => {
                if (this.state.jwt && this.state.didPreLoad) {
                  // If you're on the login page with a jwt, redirect to the default view.
                  return <DefaultRedirect />;
                }
                return (
                  <RenderLogin
                    onLoginSuccess={() => {
                      // If login succeeds, update the JWT in state
                      this.updateJwt();
                    }}
                  />
                );
              }}
              path={"/login"}
              exact
            />
            {/* Show API error if applicable */}
            <APIErrorDialog store={this.appStore} />
            {!this.state.jwt && <Redirect to="/login" />}
            {this.state.jwt && this.validUser()}
          </React.Fragment>
        )}
      </StoreContext.Provider>
    );
  }
}

/**
 * DO NOT ADD THE observer() TO THE APP
 */

export default withStyles(styles)(App);
export { App as AppClass };
